import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
};

const CARD_HEIGHT = 72; // tweak if you want taller/shorter cards

const fullCard = {
  width: '100%',
  minHeight: CARD_HEIGHT,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: '#fff',
};

const Friends = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [openDD, setOpenDD] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const [results, setResults] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [friends, setFriends] = useState([]);
  const [busy, setBusy] = useState(null);

  const debounced = useDebounced(query, 250);
  const wrapRef = useRef(null);

  // MOBILE CHECK (render-time, no listeners)
  const isMobile = (typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(max-width: 768px)').matches);

  // >>> ONLY-CHANGE: icon buttons for mobile (remove native iOS button chrome, bigger, baseline aligned)
  const iconBtn = {
    WebkitAppearance: 'none',
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    borderRadius: 0,
    padding: 0,
    margin: 0,
    lineHeight: 1,
    fontSize: 24,
    fontWeight: 400,
    color: '#0e0e0e',
    display: 'inline-flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    verticalAlign: 'text-bottom',
    cursor: 'pointer',
    userSelect: 'none',
  };
  const iconWrap = { display: 'flex', gap: 12, alignItems: 'baseline', flexShrink: 0 };

  // --- simple in-memory cache for friend profiles (bio/image) ---
  const profileCache = useRef(new Map());
  const getProfile = async (username) => {
    if (profileCache.current.has(username)) return profileCache.current.get(username);
    try {
      const r = await axios.get(`/auth/user/${encodeURIComponent(username)}`);
      const d = r.data || {};
      const prof = { bio: d.bio || '', image: d.image || '' };
      profileCache.current.set(username, prof);
      return prof;
    } catch {
      const prof = { bio: '', image: '' };
      profileCache.current.set(username, prof);
      return prof;
    }
  };

  const hydrateFriends = async (list) => {
    const enriched = await Promise.all(
      (list || []).map(async (u) => {
        const prof = await getProfile(u.username);
        return {
          ...u,
          bio: u.bio ?? prof.bio ?? '',
          image: u.image ?? prof.image ?? '',
        };
      })
    );
    return enriched;
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenDD(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const refreshFriends = async () => {
    const res = await axios.get('/friends');
    const base = res.data?.friends || [];
    const hydrated = await hydrateFriends(base);
    setFriends(hydrated);
  };
  const refreshRequests = async () => {
    const r = await axios.get('/friends/requests');
    setIncoming(r.data?.incoming || []);
    setOutgoing(r.data?.outgoing || []);
  };

  useEffect(() => {
    refreshFriends();
    refreshRequests();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!debounced.trim()) {
      setResults([]);
      return;
    }
    const run = async () => {
      const res = await axios.get(`/users/search?username=${encodeURIComponent(debounced)}`);
      const data = res.data?.results || [];
      const outgoingSet = new Set(outgoing.map((r) => r.username));
      const incomingMap = new Map(incoming.map((r) => [r.username, r.id]));
      const friendSet = new Set(friends.map((u) => u.username));
      const decorated = data.map((u) => ({
        ...u,
        isFriend: friendSet.has(u.username),
        outgoingRequestId: outgoingSet.has(u.username) ? true : null,
        incomingRequestId: incomingMap.get(u.username) || null,
      }));
      setResults(decorated);
      setOpenDD(true);
      setHighlight(0);
    };
    run().catch(() => setResults([]));
  }, [debounced]); // eslint-disable-line

  const requestFriend = async (username) => {
    try {
      setBusy(username);
      await axios.post(`/friends/requests/${encodeURIComponent(username)}`);
      await Promise.all([refreshRequests(), refreshFriends()]);
      if (debounced.trim()) {
        const res = await axios.get(`/users/search?username=${encodeURIComponent(debounced)}`);
        setResults(res.data?.results || []);
      }
      setOpenDD(false);
    } finally { setBusy(null); }
  };

  const acceptReq = async (id) => {
    try {
      setBusy(id);
      await axios.post(`/friends/requests/${id}/accept`);
      await Promise.all([refreshRequests(), refreshFriends()]);
    } finally { setBusy(null); }
  };
  const declineReq = async (id) => {
    try {
      setBusy(id);
      await axios.post(`/friends/requests/${id}/decline`);
      await refreshRequests();
    } finally { setBusy(null); }
  };

  const gotoFriend = (u) => {
    if (!u || !u.isFriend) return;
    setOpenDD(false);
    navigate(`/friends/${encodeURIComponent(u.username)}`);
  };

  const onKeyDown = (e) => {
    if (!openDD || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); gotoFriend(results[highlight]); }
  };

  // NEW: local search for your existing friends (username match)
  const [friendsQuery, setFriendsQuery] = useState(''); // NEW
  const filteredFriends = useMemo(() => {               // NEW
    const q = friendsQuery.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter(u => (u.username || '').toLowerCase().includes(q));
  }, [friendsQuery, friends]);

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          {/* Title + Search */}
          <div style={{ maxWidth: 520, margin: '10px auto 28px' }}>
            <h2 style={{ fontWeight: 900, margin: '0 0 10px', textAlign: 'left' }}>Add friends</h2>

            {/* Search + dropdown */}
            <div ref={wrapRef} style={{ position:'relative' }}>
              <input
                className="input"
                placeholder="Search by username…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length && setOpenDD(true)}
                onKeyDown={onKeyDown}
              />
              {openDD && results.length > 0 && (
                <div style={{
                  position:'absolute', left:0, right:0, top:'calc(100% + 6px)',
                  background:'#fff', border:'1px solid var(--border)', borderRadius:10,
                  boxShadow:'var(--shadow)', zIndex:20, overflow:'hidden'
                }}>
                  {results.slice(0, 8).map((r, i) => {
                    const img = r.image ? `${PUBLIC_BASE}${r.image}` : '';
                    const isIncoming = !!r.incomingRequestId;
                    const isOutgoing = !!r.outgoingRequestId;
                    return (
                      <div
                        key={r.username}
                        onMouseDown={(e) => { e.preventDefault(); if (r.isFriend) gotoFriend(r); }}
                        onMouseEnter={() => setHighlight(i)}
                        style={{
                          display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
                          padding:'10px 12px', background: i === highlight ? '#f3f4f6' : '#fff',
                          cursor:'pointer'
                        }}
                      >
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{
                            width:36, height:36, borderRadius:8, overflow:'hidden',
                            background:'#eef0f2', display:'flex', alignItems:'center', justifyContent:'center'
                          }}>
                            {img
                              ? <img src={img} alt={r.username} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                              : <span style={{ color:'#7a7a7a', fontSize:12 }}>No img</span>}
                          </div>
                          <span>@{r.username}</span>
                        </div>
                        {r.isFriend ? (
                          <button className="btn" disabled>Friends</button>
                        ) : isIncoming ? (
                          <div style={iconWrap}>
                            {isMobile ? (
                              <>
                                <button
                                  aria-label="Accept"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); acceptReq(r.incomingRequestId); }}
                                  disabled={busy === r.incomingRequestId}
                                  style={{ ...iconBtn, opacity: busy === r.incomingRequestId ? 0.5 : 1 }}
                                >
                                  ✓
                                </button>
                                <button
                                  aria-label="Decline"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); declineReq(r.incomingRequestId); }}
                                  disabled={busy === r.incomingRequestId}
                                  style={{ ...iconBtn, opacity: busy === r.incomingRequestId ? 0.5 : 1 }}
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-primary"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); acceptReq(r.incomingRequestId); }}
                                  disabled={busy === r.incomingRequestId}
                                >
                                  {busy === r.incomingRequestId ? 'Accepting…' : 'Accept'}
                                </button>
                                <button
                                  className="btn btn-ghost"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); declineReq(r.incomingRequestId); }}
                                  disabled={busy === r.incomingRequestId}
                                >
                                  Decline
                                </button>
                              </>
                            )}
                          </div>
                        ) : isOutgoing ? (
                          <button className="btn" disabled>Requested</button>
                        ) : (
                          <button
                            className="btn btn-primary"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); requestFriend(r.username); }}
                            disabled={busy === r.username}
                          >
                            {busy === r.username ? 'Requesting…' : 'Request'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Incoming requests */}
          <div style={{ maxWidth: 520, margin: '18px auto 0' }}>
            <h3 style={{ fontWeight: 900, margin: '0 0 10px', textAlign:'left' }}>Pending requests</h3>
            {incoming.length === 0 ? (
              <p style={{ color:'#9aa0a6', margin: 0, textAlign:'left' }}>No pending requests.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {incoming.map((r) => (
                  <div key={r.id} style={fullCard}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                      <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', background:'#eef0f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {r.image ? <img src={`${PUBLIC_BASE}${r.image}`} alt={r.username} style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : null}
                      </div>
                      <strong style={{ overflow:'hidden', textOverflow:'ellipsis' }}>@{r.username}</strong>
                    </div>

                    {/* Actions (mobile = icons, desktop = text buttons) */}
                    {isMobile ? (
                      <div style={iconWrap}>
                        <button
                          aria-label="Accept"
                          onClick={() => acceptReq(r.id)}
                          disabled={busy === r.id}
                          style={{ ...iconBtn, opacity: busy === r.id ? 0.5 : 1 }}
                        >
                          ✓
                        </button>
                        <button
                          aria-label="Decline"
                          onClick={() => declineReq(r.id)}
                          disabled={busy === r.id}
                          style={{ ...iconBtn, opacity: busy === r.id ? 0.5 : 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: isMobile ? 12 : 8, alignItems: 'baseline', flexShrink: 0 }}>
                        {isMobile ? (
                          <>
                            <button aria-label="Accept" onClick={() => acceptReq(r.id)} disabled={busy === r.id} style={iconBtn}>✓</button>
                            <button aria-label="Decline" onClick={() => declineReq(r.id)} disabled={busy === r.id} style={iconBtn}>✕</button>
                          </>
                        ) : (
                          <>
                            <button className="logout-btn" onClick={() => acceptReq(r.id)} disabled={busy === r.id}
                                    style={{ borderColor:'#059669', background:'#10b981', color:'#fff' }}>
                              {busy === r.id ? 'Accepting…' : 'Accept'}
                            </button>
                            <button className="logout-btn" onClick={() => declineReq(r.id)} disabled={busy === r.id}>
                              Decline
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing requests */}
          <div style={{ maxWidth: 520, margin: '24px auto 0' }}>
            <h3 style={{ fontWeight: 900, margin: '0 0 10px', textAlign:'left' }}>Sent requests</h3>
            {outgoing.length === 0 ? (
              <p style={{ color:'#9aa0a6', margin: 0, textAlign:'left' }}>No outgoing requests.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {outgoing.map((r) => (
                  <div key={r.id} style={fullCard}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                      <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', background:'#eef0f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {r.image ? <img src={`${PUBLIC_BASE}${r.image}`} alt={r.username} style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : null}
                      </div>
                      <strong style={{ overflow:'hidden', textOverflow:'ellipsis' }}>@{r.username}</strong>
                    </div>
                    <span style={{ color:'#6b7280', fontSize:12, flexShrink:0 }}>Pending</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Your friends */}
          <div style={{ maxWidth: 520, margin: '36px auto 0' }}>
            <h3 style={{ fontWeight: 900, margin: '0 0 10px', textAlign:'left' }}>Your friends</h3>

            {/* NEW: search bar (same styling as above), filters your existing friends */}
            <input
              className="input"                // same visual style as “Add friends” search
              placeholder="Search your friends…"
              value={friendsQuery}
              onChange={(e) => setFriendsQuery(e.target.value)}
              aria-label="Search your friends"
              style={{ marginBottom: 12 }}     // subtle spacing above the list
            />

            {friends.length === 0 ? (
              <p style={{ color:'#9aa0a6', margin: 0, textAlign:'left' }}>
                No friends yet. Use the search above to send a request.
              </p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {filteredFriends.length === 0 ? (
                  <div style={{ color:'#9aa0a6', fontSize: 14 }}>No matching friends.</div>
                ) : (
                  filteredFriends.map((u) => (
                    <div
                      key={u.username}
                      style={{ ...fullCard, cursor:'pointer' }}
                      onClick={() => navigate(`/friends/${encodeURIComponent(u.username)}`)}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                        <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', background:'#eef0f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {u.image ? (
                            <img src={`${PUBLIC_BASE}${u.image}`} alt={u.username} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                          ) : null}
                        </div>
                        <div style={{ overflow:'hidden' }}>
                          <div style={{ fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>@{u.username}</div>
                          {u.bio ? (
                            <div style={{ color:'#6b7280', fontSize:12, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                              {u.bio}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ color:'#6b7280', fontSize:12 }}>Friend</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Friends;
